import { v4 as uuidv4 } from 'uuid';
import { ttdata, portfolio } from '~api';
import dayjs from 'dayjs';

const SET_TOKEN = 'SET_TOKEN';
const SET_API_KEY = 'SET_API_KEY';
const SET_POSITIONS = 'SET_POSITIONS';
const SET_SAVED_POSITIONS = 'SET_SAVED_POSITIONS';
const SET_LIST = 'SET_LIST';
const SET_SECRET_KEY = 'SET_SECRET_KEY';

const token = window.localStorage.getItem('tt-token') || null;
const secretKey = window.localStorage.getItem('tt-secret') || false;
const apiKey = secretKey ? secretKey.split(':')[0] : null;

const state = {
    token,
    publicKeys: [],
    apiKey,
    secretKey,
    positions: [],
    savedPositions: [],
    list: []
};

const actions = {
    /**
     * Get positions.
     */
    async getPositions({ commit, state, dispatch }) {
        if (!state.apiKey) await dispatch('setApiKey');
        if (!state.token) await dispatch('setToken');

        const uuid = uuidv4();
        const { token, apiKey } = state;
        const resp = await ttdata.getPositions(token, apiKey, uuid);

        if (resp && resp.status === 'Ok') {
            commit(SET_POSITIONS, resp.positions);
        } else {
            if (state.secretKey) {
                dispatch('refreshToken');
                dispatch('getPositions');
            }
        }

        return true;
    },
    /**
     * Get positions.
     */
    async getSavedPositions({ commit }) {
        const respSavedPositions = await portfolio.getPortfolioWatchlist();

        if (respSavedPositions)
            commit(SET_SAVED_POSITIONS, respSavedPositions.data);

        return true;
    },
    /**
     * Set secret key positions.
     */
    async setSecretKey({ commit }) {
        const secretKey = window.localStorage.getItem('tt-secret') || null;
        commit(SET_SECRET_KEY, secretKey);
    },
    /**
     * Set API key.
     */
    async setApiKey({ commit }) {
        const secret = window.localStorage.getItem('tt-secret');

        if (!secret) return false;

        const apiKey = secret.split(':')[0];

        commit(SET_API_KEY, apiKey);

        return apiKey;
    },
    /**
     * Set token.
     */
    async setToken({ commit, dispatch }) {
        const token = window.localStorage.getItem('tt-token');

        if (token) return commit(SET_TOKEN, token);

        return await dispatch('getToken');
    },

    /**
     * Get token
     */
    async getToken({ commit, state, dispatch }) {
        if (!state.apiKey) await dispatch('setApiKey');

        const { secretKey, apiKey } = state;
        const resp = await ttdata.getToken(secretKey, apiKey);

        if (resp) {
            window.localStorage.setItem('tt-token', resp);
            commit(SET_TOKEN, resp);
        }

        return resp;
    },
    /**
     * Get list
     */
    async getList({ commit, state, dispatch }, refresh = false) {
        await dispatch('getPositions');

        const savedListDate = window.localStorage.getItem('tt-list-date');

        if (!refresh && savedListDate) {
            const diff = dayjs().diff(dayjs(savedListDate), 'h');
            const savedList = window.localStorage.getItem(
                'tt-list-instruments'
            );

            if (diff < 24 && savedList) {
                return commit(SET_LIST, JSON.parse(savedList));
            }
        }

        let listInstruments = [];
        const { positions, apiKey, token } = state;

        for (const position in positions) {
            const resp = await ttdata.getData(
                token,
                apiKey,
                positions[position].instrumentId
            );

            if (resp && resp.status === 'Ok') {
                listInstruments.push(resp.instrument[0]);
            } else {
                if (state.secretKey) {
                    dispatch('refreshToken');
                    dispatch('getList');
                }
            }

            window.localStorage.setItem(
                'tt-list-instruments',
                JSON.stringify(listInstruments)
            );
            window.localStorage.setItem('tt-list-date', dayjs());
        }

        commit(SET_LIST, listInstruments);

        return true;
    },
    async refreshToken({ commit }) {
        window.localStorage.removeItem('tt-token');
        commit(SET_TOKEN, '');
        commit(SET_POSITIONS, '');
        return true;
    }
};

const mutations = {
    [SET_TOKEN](state, token) {
        state.token = token;
    },
    [SET_API_KEY](state, key) {
        state.apiKey = key;
    },
    [SET_POSITIONS](state, positions) {
        state.positions = positions;
    },
    [SET_SAVED_POSITIONS](state, positions) {
        state.savedPositions = positions;
    },
    [SET_SECRET_KEY](state, key) {
        state.secretKey = key;
    },
    [SET_LIST](state, listInstruments) {
        let list = [];
        const { positions, savedPositions } = state;

        for (const position in positions) {
            list.push({
                ...positions[position],
                ...listInstruments.find(
                    row => row.id === positions[position].instrumentId
                )
            });
        }

        let sortedList = [];
        if (savedPositions?.length) {
            savedPositions.forEach(savedId => {
                sortedList.push(
                    list.find(item => item.instrumentId === savedId)
                );
            });
            sortedList = [
                ...sortedList,
                ...list.filter(
                    item => !state.savedPositions.includes(item.instrumentId)
                )
            ];
        }

        state.list = sortedList;
    }
};

const getters = {
    list: state => state.list,
    secretKey: state => state.secretKey,
    positionsList: state =>
        state.list.map(item => ({
            text: item.name,
            value: item.instrumentId
        })),
    positionsListDictionary: state => {
        const list = new Map();
        state.list.forEach(item =>
            list.set(item.instrumentId, { name: item.name })
        );

        return Object.fromEntries(list);
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};
